<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { Alert, Button, Card, Input, Spinner } from '@/components/ui'
import SetupFlowShell from '@/components/shared/SetupFlowShell.vue'
import { loginUser } from '@/api/authClient'
import { track } from '@/lib/usageTracker'
import { useAuthStore } from '@/stores/authStore'

// --- Interface-design checkpoint ---
// Intent: store-owner entry surface. Provenance chips name the acting
// domain (mobile user) and the next step (store selection). Calm, paper
// background, ink-navy text, receipt-green for success, stamp-red for
// failure. Compact density, no marketing copy. Reuse existing Card /
// Input / Button / Alert / Spinner primitives.

const route = useRoute()
const router = useRouter()
const store = useAuthStore()

const email = ref('')
const password = ref('')
const loading = ref(false)
const authError = ref(false)
const networkError = ref(false)
const errorMessage = ref('We could not verify those credentials.')

const hasQueryFallback = computed(() => !!(route.query.invoiceNumber && route.query.storeID))

const contextItems = computed(() => [
  {
    label: 'Acting context',
    value: store.user?.email ? `Signed in as ${store.user.email}` : 'Not signed in',
    tone: store.user?.email ? 'success' : 'default',
  },
  {
    label: 'Store reference',
    value: store.selectedStore?.storeName
      ? `${store.selectedStore.storeName} (${store.selectedStore.storeDb})`
      : 'Choose after sign-in',
    tone: store.selectedStore ? 'default' : 'warning',
  },
  {
    label: 'Verification state',
    value: loading.value ? 'Checking credentials' : (authError.value || networkError.value) ? 'Action needed' : 'Awaiting sign-in',
    tone: loading.value ? 'accent' : (authError.value ? 'error' : (networkError.value ? 'warning' : 'default')),
  },
])

function resetUI() {
  loading.value = false
  authError.value = false
  networkError.value = false
}

async function submit() {
  track('form:UserLogin', 'submit')
  resetUI()
  if (!email.value.trim() || !password.value) {
    authError.value = true
    errorMessage.value = 'Enter both your email and password to continue.'
    return
  }
  try {
    loading.value = true
    const response = await loginUser({ email: email.value.trim(), password: password.value })
    store.setUserContext(response.user, response.stores)
    store.setQueryParams({
      invoiceNumber: route.query.invoiceNumber?.toString(),
      storeID: route.query.storeID?.toString() || route.query.storeId?.toString(),
    })
    router.replace({ name: 'StoreSelection' })
  } catch (e) {
    const msg = String((e as Error)?.message || e)
    if (/failed to fetch|network|TypeError/i.test(msg)) {
      networkError.value = true
    } else if (/401|403|HTTP\s*40[13]/i.test(msg)) {
      authError.value = true
      errorMessage.value = 'Email or password is incorrect. Try again or contact your administrator.'
    } else {
      authError.value = true
      errorMessage.value = msg
    }
    console.error(e)
  } finally {
    loading.value = false
  }
}

onMounted(() => {
  // If a query-param setup link brought the user here, surface that
  // they could also open the legacy setup flow.
  if (hasQueryFallback.value) {
    store.setQueryParams({
      invoiceNumber: route.query.invoiceNumber?.toString(),
      storeID: route.query.storeID?.toString() || route.query.storeId?.toString(),
    })
  }
})
</script>

<template>
  <SetupFlowShell
    eyebrow="Store owner sign-in"
    title="Confirm your mobile identity"
    subtitle="Use the email and password you use on the mobile app to access the store accounting workspace."
    :current-step="1"
    :steps="['Sign in', 'Choose store', 'Connect', 'Workspace']"
    :context-items="contextItems"
  >
    <Card padding="lg" class="login-panel">
      <div class="login-panel__actions-row">
        <Button
          v-if="!loading"
          variant="secondary"
          id="legacyInviteButton"
          @click="() => { track('btn:OpenInviteLink', 'click'); router.push({ name: 'SetupEntry', query: route.query }) }"
        >
          Have an invite link?
        </Button>
      </div>

      <form class="login-panel__form" @submit.prevent="submit">
        <div class="login-panel__field">
          <label for="loginEmail" class="login-panel__label">Email</label>
          <Input
            id="loginEmail"
            v-model="email"
            type="email"
            autocomplete="email"
            placeholder="owner@example.com"
            required
          />
        </div>
        <div class="login-panel__field">
          <label for="loginPassword" class="login-panel__label">Password</label>
          <Input
            id="loginPassword"
            v-model="password"
            type="password"
            autocomplete="current-password"
            placeholder="Enter your mobile app password"
            required
          />
        </div>
        <div class="login-panel__actions">
          <Button type="submit" id="loginSubmit" :disabled="loading">
            <Spinner v-if="loading" size="sm" />
            <span v-if="!loading">Sign in and continue</span>
            <span v-else>Verifying credentials</span>
          </Button>
        </div>
      </form>

      <Card v-if="loading" padding="md" class="login-state">
        <Spinner size="md" />
        <div>
          <div class="login-state__title">Looking up your mobile identity</div>
          <p class="login-state__copy">
            Checking your email and password against the mobile database. We never expose your password
            to the browser; verification happens server-side.
          </p>
        </div>
      </Card>

      <Alert v-else-if="networkError" id="loginNetworkError" variant="warning" title="Sign-in service unreachable">
        We couldn't reach the sign-in service. Check your connection and try again.
        <div class="login-panel__alert-actions">
          <Button id="retryNetwork" @click="submit">Retry sign-in</Button>
        </div>
      </Alert>

      <Alert v-else-if="authError" id="loginAuthError" variant="error" title="Sign-in failed">
        {{ errorMessage }}
        <div class="login-panel__alert-actions">
          <Button id="retryAuth" @click="submit">Try again</Button>
        </div>
      </Alert>
    </Card>
  </SetupFlowShell>
</template>

<style scoped>
.login-panel {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.login-panel__actions-row {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 0.5rem;
}

.login-panel__label {
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-ink-soft);
}

.login-panel__form {
  display: flex;
  flex-direction: column;
  gap: 0.8rem;
  border: 1px solid var(--color-border-soft);
  background: var(--color-surface-muted);
  border-radius: var(--radius-sm);
  padding: 1rem;
}

.login-panel__field {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.login-panel__actions {
  display: flex;
  justify-content: flex-end;
}

.login-panel__alert-actions {
  margin-top: 0.6rem;
}

.login-state {
  display: flex;
  align-items: center;
  gap: 0.9rem;
}

.login-state__title {
  font-size: 0.98rem;
  font-weight: 700;
  color: var(--color-ink);
}

.login-state__copy {
  margin: 0.35rem 0 0;
  max-width: 54ch;
  color: var(--color-ink-soft);
  font-size: 0.88rem;
}

@media (max-width: 640px) {
  .login-panel__actions :deep(button) {
    width: 100%;
    justify-content: center;
  }
}
</style>
